import type { EditControlConfig } from '../declare'
import { useState, useEffect, useContext } from 'react'
import { autorun, toJS } from 'mobx'
import { Form } from 'antd'
import * as Row from './row'
import { EditContext } from 'src/components'
import { controlFieldVerify } from 'src/components/root/helper'
import { $consts } from 'src/plugins'
import { get } from 'lodash'

/**
 * 编辑表单子表行编辑控件
 * @param props 
 * @returns 
 */
const RowControl = (props: { config: EditControlConfig }) => {
  const edit = useContext(EditContext)

  // 控件值
  const [value, setValue] = useState()
  useEffect(() => autorun(() => {
    const { __CONTEXT__: editTable, __RECORD__: record, FIELD_NAME } = props.config
    const { dataSource = [] } = editTable.activeTableData ?? {}
    const find = dataSource.find((item: any) => item.ID === record.ID)
    setValue(get(find, FIELD_NAME))
  }), [props.config])

  // 控件 change 事件
  const handleChange = (value: any, extra: any, payload: any) => {
    const { __CONTEXT__: editTable, __RECORD__: record, FIELD_NAME, CHANGE_FUNC } = props.config
    const copyDataSource = toJS(editTable.activeTableData.dataSource)
    const copyRecord = copyDataSource.find((item: any) => item.ID === record.ID)
    // 往 __RECORD__ 里面添加 __VERIFY__ 校验结果
    Object.assign(copyRecord, {
      [FIELD_NAME]: value,
      ...extra,
      __VERIFY__: {
        ...copyRecord.__VERIFY__,
        [FIELD_NAME]: controlFieldVerify(props.config, value)
      }
    })
    editTable.activeTable.setDataSource(copyDataSource)
    // 是否配置了动态加载 js 中的方法名
    const dynamicPayload = { value, extra, config: props.config, payload }
    edit?.dynamicJs[CHANGE_FUNC]?.(edit, FIELD_NAME, dynamicPayload)
  }

  // 根据 CONTROL_TYPE 获取对应控件
  const getControl = () => {
    const { CONTROL_TYPE } = props.config
    switch (CONTROL_TYPE) {
      // 文本输入框
      case $consts['TABLE/CONTROL_TYPE_TEXTINPUT']:
        return (
          <Row.TextInput
            value={value}
            config={props.config}
            onChange={handleChange}
          />
        )
      // 时间控件类型
      case $consts['TABLE/CONTROL_TYPE_DATE_SECOND']:
      case $consts['TABLE/CONTROL_TYPE_DATE_DAY_SECOND']:
      case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY_SECOND']:
      case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY_MINUTE']:
      case $consts['TABLE/CONTROL_TYPE_DATE_DAY']:
      case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_DAY']:
      case $consts['TABLE/CONTROL_TYPE_DATE_MONTH']:
      case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_MONTH']:
      case $consts['TABLE/CONTROL_TYPE_DATE_YEAR']:
      case $consts['TABLE/CONTROL_TYPE_DATE_RANGE_YEAR']:
        return (
          <Row.DateControl
            value={value}
            config={props.config}
            onChange={handleChange}
          />
        )
      // 枚举下拉单选框
      case $consts['TABLE/CONTROL_TYPE_ENUM_SELECT']:
        return (
          <Row.EnumSelect
            value={value}
            config={props.config}
            onChange={handleChange}
          />
        )
      // 单选 Switch 控件
      case $consts['TABLE/CONTROL_TYPE_SWITCH']:
        return (
          <Row.EnumSelect
            value={value}
            config={props.config}
            onChange={handleChange}
          />
        )
      // 复选框控件
      case $consts['TABLE/CONTROL_TYPE_CHECKBOX']:
        return (
          <Row.CheckboxControl
            value={value}
            config={props.config}
            onChange={handleChange}
          />
        )
      // 密码控件
      case $consts['TABLE/CONTROL_TYPE_PASSWORD']:
        return (
          <Row.PasswordInput
            value={value}
            config={props.config}
            onChange={handleChange}
          />
        )
      // 分页下拉
      case $consts['TABLE/CONTROL_TYPE_PAGINATION_SELECT']:
        return (
          <Row.PaginationSelect
            value={value}
            config={props.config}
            onChange={handleChange}
          />
        )
      // 图片/文件选择
      case $consts['TABLE/CONTROL_TYPE_FILE_SELECT']:
        return (
          <Row.FileSelect
            value={value}
            config={props.config}
            onChange={handleChange}
          />
        )
      // 数字输入框：小数精度
      case $consts['TABLE/CONTROL_TYPE_NUMBERINPUT']:
      case $consts['TABLE/CONTROL_TYPE_NUMBERINPUT_TWODOT']:
      case $consts['TABLE/CONTROL_TYPE_NUMBERINPUT_THREEDOT']:
        return (
          <Row.NumberInput
            value={value}
            config={props.config}
            onChange={handleChange}
          />
        )
      // 文件上传
      case $consts['TABLE/CONTROL_TYPE_FILE_UPLOAD']:
        return (
          <Row.FileUpload
            value={value}
            config={props.config}
            onChange={handleChange}
          />
        )
      default:
        return <div />
    }
  }

  const { __RECORD__: record, FIELD_NAME } = props.config
  const { __VERIFY__: verify } = record
  const status = verify?.[FIELD_NAME]?.invalid ? 'error' : undefined

  return (
    <Form.Item validateStatus={status}>
      {getControl()}
    </Form.Item>
  )
}

export default RowControl